import type { ReactNode } from "react";

export type ChoiceVariant = "idle" | "correct" | "wrong" | "revealed-correct" | "dimmed";

type Props = {
  index: number;
  german: string;
  variant: ChoiceVariant;
  disabled: boolean;
  onClick: () => void;
};

const VARIANT_CLASSES: Record<ChoiceVariant, string> = {
  idle: "bg-surface hover:bg-card border-outline text-fg active:bg-card",
  correct: "bg-success-soft border-success text-success",
  wrong: "bg-danger-soft border-danger text-danger",
  "revealed-correct": "bg-success-soft border-success text-success",
  dimmed: "bg-surface/40 border-outline-soft text-fg-faint",
};

const BADGE_VARIANT_CLASSES: Record<ChoiceVariant, string> = {
  idle: "bg-bg/80 border-outline text-fg-muted",
  correct: "bg-success/20 border-success text-success",
  wrong: "bg-danger/20 border-danger text-danger",
  "revealed-correct": "bg-success/20 border-success text-success",
  dimmed: "bg-bg/40 border-outline-soft text-fg-faintest",
};

export function ChoiceButton({ index, german, variant, disabled, onClick }: Props) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid="choice"
      className={`group w-full text-left px-4 py-3 min-h-[60px] rounded-xl border transition-all duration-150 flex items-center gap-3 text-base ${VARIANT_CLASSES[variant]} disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-bg active:scale-[0.985]`}
    >
      <Badge variantClass={BADGE_VARIANT_CLASSES[variant]}>{index}</Badge>
      <span className="flex-1 text-base leading-snug">{german}</span>
    </button>
  );
}

function Badge({ children, variantClass }: { children: ReactNode; variantClass: string }) {
  return (
    <span
      className={`inline-flex shrink-0 items-center justify-center w-7 h-7 rounded-lg border text-xs font-mono font-semibold transition-colors ${variantClass}`}
    >
      {children}
    </span>
  );
}
